/* eslint-disable react/prop-types */
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import SaveIcon from '@mui/icons-material/Save';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LoadingButton from '@mui/lab/LoadingButton';

import {
  Autocomplete,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  Close,
} from '@mui/icons-material';
import moment from 'moment';
import LocationAutocomplete from '../../../../../components/Autocompletes/Locations';
import HorseService from '../../../../../services/HorseService';
import { getAllBarnHorses } from '../../../../../redux/barn/actions';
import {
  addHorseLocation,
  updateHorse,
} from '../../../../../redux/horse/actions';
import theme from '../../../../../utils/theme';
import { blueGrey } from '../../../../../utils/brandColors';
import Horse from '../../../../../components/Horse';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const AddHorseModalComponent = ({
  open,
  onClose,
  horse,
  navigate,
}) => {
  const dispatch = useDispatch();
  const fullscreen = useMediaQuery(theme.breakpoints.down('md'));

  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const allLocations = useSelector((state) => state.location.allLocations);
  const allReferenceNumbers = useSelector((state) => state.barn.referenceNumbers);

  const [existingHorseId, setExistingHorseId] = React.useState(null);
  const [isAddingHorse, setIsAddingHorse] = React.useState(false);
  const [addingHorseError, setAddingHorseError] = React.useState(null);

  // form fields
  const [usefHorse, setUsefHorse] = React.useState(null);
  const [usefId, setUsefId] = React.useState('');
  const [referenceNumbers, setReferenceNumbers] = React.useState({});
  const [name, setName] = React.useState('');
  const [breed, setBreed] = React.useState('');
  const [hands, setHands] = React.useState('');
  const [color, setColor] = React.useState('');
  const [stall, setStall] = React.useState('');
  const [nameValidation, setNameValidation] = React.useState('');
  const [showName, setShowName] = React.useState('');
  const [showNameValidation, setShowNameValidation] = React.useState('');
  const [sex, setSex] = React.useState('');
  const [dateOfBirth, setDateOfBirth] = React.useState(null);
  const [microchipNumber, setMicrochipNumber] = React.useState('');
  const [sexValidation, setSexValidation] = React.useState('');
  const [location, setLocation] = React.useState(null);
  const [lodgingStartDate, setLodgingStartDate] = React.useState(new Date());
  const [cannotLodgeError, setCannotLodgeError] = React.useState('');
  // const [lodgingStartDateValidation, setLodgingStartDateValidation] = React.useState(null);
  // const [lodgingEndDate, setLodgingEndDate] = React.useState(null);
  // const [lodgingEndDateValidation, setLodgingEndDateValidation] = React.useState(null);

  const populateHorseFields = (horseData = {}) => {
    setUsefHorse(null);
    setUsefId(horseData.usefId || '');
    setReferenceNumbers(horseData.referenceNumbers || {});
    setName(horseData.name || '');
    setShowName(horseData.showName || '');
    setSex(horseData.sex || '');
    setBreed(horseData.breed || '');
    setHands(horseData.hands || '');
    setColor(horseData.color || '');
    setStall(horseData.stall || '');
    setDateOfBirth(horseData.dateOfBirth || null);
    setMicrochipNumber(horseData.microchipNumber || '');
    setLodgingStartDate(horseData.lodgingStartDate || new Date());
    setLocation(((!horseData || !horseData.id) && allLocations && allLocations.length === 1)
      ? allLocations[0]
      : null);
    setSexValidation(null);
    setNameValidation(null);
    setShowNameValidation(null);
    setAddingHorseError(null);
  };

  React.useEffect(() => {
    setExistingHorseId(null);
    setAddingHorseError(null);
    setIsAddingHorse(false);
    setCannotLodgeError(false);
    populateHorseFields(horse || {});
  }, [open]);

  const lookupHorseByUsefId = (e) => {
    const idToLookup = e.target.value;
    if (idToLookup && idToLookup.length >= 2) {
      HorseService.getHorse(e.target.value).then((data) => {
        // eslint-disable-next-line no-console
        setUsefHorse(data);
        setExistingHorseId(data.id);
      }).catch((error) => {
        setUsefHorse(null);
        setExistingHorseId(null);
        console.error(error);
      });
    } else {
      setUsefHorse(null);
    }
  };

  const validateFields = () => {
    if (name.length < 1 || name.length > 100) {
      setNameValidation('Name must be between 1 and 100 characters in length.');
      return false;
    } setNameValidation(null);
    if (showName && showName.length > 0 && (showName.length < 1 || showName.length > 100)) {
      setShowNameValidation('Show Name must be between 1 and 100 characters in length.');
      return false;
    } setShowNameValidation(null);
    if (!sex || sex.length <= 0) {
      setSexValidation('Select a sex.');
      return false;
    } setSexValidation(null);
    return true;
  };

  const onSave = () => {
    if (!validateFields()) { return; }

    setAddingHorseError(null);
    setIsAddingHorse(true);

    const request = {
      lodgeHorseWithBarn: true,
      usefId: usefId || null,
      referenceNumbers: referenceNumbers || null,
      name,
      showName: showName || null,
      sex,
      breed: breed || null,
      hands: hands || null,
      color: color || null,
      stall: stall || null,
      microchipNumber: microchipNumber || null,
      dateOfBirth,
      lodgingStartDate,
    };

    if (horse && horse.id) {
      // if updating horse already at barn, remove some fields
      delete request.lodgeHorseWithBarn;
      delete request.lodgingStartDate;
    }

    (!(existingHorseId || (horse && horse.id))
      ? HorseService.addHorse(request)
      : dispatch(updateHorse(existingHorseId || horse.id, request)))
      .then((data) => {
        if (location && (!horse || !horse.id)) {
          // add horse location if new horse
          dispatch(addHorseLocation(data.id, location.id, {
            startDate: new Date(),
            endDate: null,
          })).then(() => {
            setIsAddingHorse(false);
            dispatch(getAllBarnHorses(selectedBarn.id)).then((newAllBarnHorses) => {
              const foundNewHorse = newAllBarnHorses.find((bh) => bh.horse.id === data.id);
              onClose((!horse || !horse.id)
                ? (foundNewHorse.horse || data)
                : null);
            });
          }).catch((error) => {
            setAddingHorseError(error.message || 'There was an error attempting to add the horse location. Please contact Questri support.');
            console.error(error);
            setIsAddingHorse(false);
            onClose();
          });
        } else if ((!horse || !horse.id)) {
          setIsAddingHorse(false);
          dispatch(getAllBarnHorses(selectedBarn.id)).then((newAllBarnHorses) => {
            const foundNewHorse = newAllBarnHorses.find((bh) => bh.horse.id === data.id);
            onClose((!horse || !horse.id)
              ? (foundNewHorse.horse || data)
              : null);
          });
        } else {
          setIsAddingHorse(false);
          onClose(data);
        }
      }).catch((error) => {
        setIsAddingHorse(false);
        setAddingHorseError(error.message || 'There was an error attempting to add this horse. Please contact Questri support.');
        console.error(error);
        if (error.message && error.message.indexOf('lodges with another barn') > 0) {
          // Horse is lodging at another barn
          setCannotLodgeError(true);
        }
      });
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      open={open}
      PaperProps={{
        sx: {
          m: 0,
          mx: fullscreen
            ? 0
            : 2,
          width: '100%',
          maxWidth: 1200,
        },
      }}
      fullScreen={fullscreen}
      onClose={() => onClose()}
      aria-labelledby="modal-add-horse-title"
      aria-describedby="modal-add-horse-description"
    >
      <DialogTitle>
        <Typography
          id="modal-add-horse-title"
          variant="h6"
          component="h2"
          align="center"
          gutterBottom
        >
          {!(horse && horse.id)
            ? 'Add'
            : 'Edit'} Horse
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          paddingBottom: 0,
          p: 2,
        }}
      >
        {
          horse && horse.id && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Horse
                horse={horse}
                showName
                showShowName
              />
            </Box>
          )
        }
        <Grid
          container
          spacing={2}
          sx={{ pt: 1 }}
        >
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              id="name-field"
              label="Name"
              value={name}
              onChange={(e) => { setName(e.target.value); }}
              disabled={isAddingHorse}
              error={!!nameValidation}
              helperText={nameValidation && nameValidation}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required={false}
              id="show-name-field"
              label="Show Name"
              value={showName}
              onChange={(e) => { setShowName(e.target.value); }}
              disabled={isAddingHorse}
              error={!!showNameValidation}
              helperText={showNameValidation && showNameValidation}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl
              error={!!sexValidation}
              required
              fullWidth={fullscreen}
              sx={{
                ml: 1,
                flexDirection: 'row',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <FormLabel
                id="horse-sex-radio-buttons-group-label"
                sx={{
                  margin: 0,
                  mb: 1,
                  fontSize: 20,
                }}
              >Sex
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="horse-sex-radio-buttons-group-label"
                name="horse-sex-buttons-group"
                onChange={(e) => { setSex(e.target.value); }}
                value={sex}
              >
                <FormControlLabel disabled={isAddingHorse} value="Mare" control={<Radio />} label="Mare" />
                <FormControlLabel disabled={isAddingHorse} value="Gelding" control={<Radio />} label="Gelding" />
                <FormControlLabel disabled={isAddingHorse} value="Stallion" control={<Radio />} label="Stallion" />
              </RadioGroup>
              <FormHelperText sx={{ mb: 1 }}>{sexValidation && sexValidation}</FormHelperText>
            </FormControl>
          </Grid>

          {
            (!horse || !horse.id) && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    borderRadius: 2,
                    backgroundColor: blueGrey[50],
                    p: 0.5,
                    pl: 0,
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <Typography sx={{ ml: 2 }}>Location</Typography>
                </Box>
                <LocationAutocomplete
                  onSelect={(e, value) => { setLocation(value); }}
                  value={location}
                  multiple={false}
                  helperText="The location of this horse"
                />
              </Grid>
            )
          }
          <Grid item xs={12}>
            <Box
              sx={{
                borderRadius: 2,
                backgroundColor: blueGrey[50],
                p: 0.5,
                pl: 0,
                mt: 2,
                mb: 0.5,
              }}
            >
              <Typography sx={{ ml: 2 }}>Reference IDs</Typography>
            </Box>
            <Box
              component="form"
              sx={{
                '& .MuiTextField-root': {
                  m: 1,
                  ml: 0,
                },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Autocomplete
                margin="dense"
                size="small"
                disabled
                freeSolo
                options={['USEF ID']}
                sx={{
                  width: '23ch',
                  mr: 2,
                  ml: 0,
                }}
                renderInput={(params) => <TextField {...params} autoFocus label="Reference ID Name" />}
                value="USEF ID"
              />
              <TextField
                margin="dense"
                size="small"
                id="usef-id-field"
                label="Value"
                disabled={isAddingHorse || !!existingHorseId}
                value={usefId}
                onBlur={lookupHorseByUsefId}
                onChange={(e) => { setUsefId(e.target.value); }}
              />
            </Box>
            {usefHorse
              && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    m: 1,
                    fontSize: 14,
                    lineHeight: '16px',
                  }}
                >
                  A horse with this USEF ID already exists in the Questri database.
                  If this horse is not currently lodging with another barn you can populate the fields below with this horses information.
                  <Button sx={{ ml: 1 }} size="small" variant="contained" onClick={() => { populateHorseFields(usefHorse); }}>Populate Fields</Button>
                </Typography>
              )}
            <Box>
              {
                Object.keys(referenceNumbers).map((num) => (
                  <Box
                    key={`ref-number-${num}`}
                    component="form"
                    sx={{
                      '& .MuiTextField-root': {
                        m: 1,
                        ml: 0,
                      },
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Autocomplete
                      id={`ref-num-key-${num}`}
                      freeSolo
                      openOnFocus
                      autoFocus={!num || (num && num.indexOf('Update') >= 0)}
                      margin="dense"
                      size="small"
                      defaultValue={num}
                      value={num}
                      options={allReferenceNumbers.filter((o) => !referenceNumbers[o])}
                      renderInput={(params) => <TextField {...params} autoFocus label="Reference ID Name" />}
                      sx={{
                        width: '23ch',
                        mr: 2,
                        ml: 0,
                      }}
                      onBlur={(e) => {
                        const deepCopy = JSON.parse(JSON.stringify(referenceNumbers)); // deep copy
                        const currentVal = JSON.parse(JSON.stringify(referenceNumbers[num])); // deep copy
                        // first remove old key
                        delete deepCopy[num];

                        setReferenceNumbers({
                          ...deepCopy,
                          [e.target.value]: currentVal,
                        })
                      }}
                    />
                    <TextField
                      margin="dense"
                      size="small"
                      variant="outlined"
                      id={`ref-num-val-${num}`}
                      value={referenceNumbers[num]}
                      onChange={(e) => setReferenceNumbers({
                        ...referenceNumbers,
                        [num]: e.target.value,
                      })}
                      label="Value"
                      sx={{ width: '23ch ' }}
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        const deepCopy = JSON.parse(JSON.stringify(referenceNumbers)); // deep copy
                        delete deepCopy[num];
                        setReferenceNumbers(deepCopy);
                      }}
                      startIcon={<Close color="danger" />}
                    >Remove
                    </Button>
                  </Box>
                ))
              }
            </Box>

            <Box
              sx={{
                mt: 1,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setReferenceNumbers({
                    ...referenceNumbers,
                    [`Update this...${Object.keys(referenceNumbers).map(() => '.')}`]: '',
                  });
                }}
                startIcon={<Add />}
              >Add New Reference Number
              </Button>
              {
                allReferenceNumbers.filter((o) => !referenceNumbers[o]).map((refNum) => (
                  <Chip
                    key={`ref-num-key-${refNum}`}
                    size="small"
                    label={refNum}
                    onClick={() => {
                      setReferenceNumbers({
                        ...referenceNumbers,
                        [`${refNum}`]: '',
                      });
                    }}
                  />
                ))
              }
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                borderRadius: 2,
                backgroundColor: blueGrey[50],
                p: 0.5,
                pl: 0,
                mt: 2,
                mb: 2,
              }}
            >
              <Typography sx={{ ml: 2 }}>Data Fields</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid
                item
                xs={fullscreen
                  ? 12
                  : undefined}
              >
                <TextField
                  fullWidth={fullscreen}
                  id="breed-field"
                  label="Breed"
                  value={breed}
                  onChange={(e) => { setBreed(e.target.value); }}
                  disabled={isAddingHorse}
                />
              </Grid>
              <Grid
                item
                xs={fullscreen
                  ? 12
                  : undefined}
              >
                <TextField
                  fullWidth={fullscreen}
                  id="height-field"
                  label="Height (hands)"
                  value={hands}
                  onChange={(e) => { setHands(e.target.value); }}
                  disabled={isAddingHorse}
                />
              </Grid>
              <Grid
                item
                xs={fullscreen
                  ? 12
                  : undefined}
              >
                <TextField
                  fullWidth={fullscreen}
                  id="color-field"
                  label="Color"
                  value={color}
                  onChange={(e) => { setColor(e.target.value); }}
                  disabled={isAddingHorse}
                />
              </Grid>
              <Grid
                item
                xs={fullscreen
                  ? 12
                  : undefined}
              >
                <TextField
                  fullWidth={fullscreen}
                  id="stall-field"
                  label="Stall"
                  value={stall}
                  onChange={(e) => { setStall(e.target.value); }}
                  disabled={isAddingHorse}
                />
              </Grid>
              <Grid
                item
                xs={fullscreen
                  ? 12
                  : undefined}
              >
                <TextField
                  fullWidth={fullscreen}
                  id="microchip-number-field"
                  label="Microchip Number"
                  value={microchipNumber}
                  onChange={(e) => { setMicrochipNumber(e.target.value); }}
                  disabled={isAddingHorse}
                />
              </Grid>
              <Grid
                item
                xs={fullscreen
                  ? 12
                  : undefined}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Date of Birth"
                    value={dateOfBirth}
                    disabled={isAddingHorse}
                    onChange={(newValue) => {
                      setDateOfBirth(moment(newValue).add(12, 'hours').toDate());
                    }}
                    renderInput={(params) => (
                      <TextField
                        fullWidth={fullscreen}
                        {...params}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ padding: '.5rem' }} />
        {
          !horse && (
            <Typography variant="caption" component="div" gutterBottom>
              &#42;Additional data can be provided after the horse is created including feed, notes, and more.
            </Typography>
          )
        }
        {
          addingHorseError && <Typography variant="caption" color="error" gutterBottom>{addingHorseError}</Typography>
        }
        {
          (addingHorseError && cannotLodgeError) && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
              }}
            ><Button size="small" color="secondary" variant="contained" onClick={() => navigate(`/horses/${existingHorseId}`)}>Request Access To Horse</Button>
            </Box>
          )
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={isAddingHorse && (!usefHorse && !isAddingHorse)}>Close</Button>
        <LoadingButton onClick={onSave} disabled={isAddingHorse && (!usefHorse && !isAddingHorse)} variant="contained" color="primary" loading={isAddingHorse} loadingPosition="start" startIcon={<SaveIcon />}>Save</LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

AddHorseModalComponent.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

AddHorseModalComponent.defaultProps = { open: false };

export default (AddHorseModalComponent);
